// ==========================================
// src/pages/EditarCarga.jsx
// EDITAR CARGA — VERSÃO COM RPC PRIMÁRIO
// ==========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { 
  ArrowLeft, Save, Loader2, Package, MapPin, Truck, 
  Calendar, DollarSign, Weight, Info
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from '@/lib/supabase';

const estadosBrasil = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const tiposVeiculo = [
  { value: 'truck', label: 'Truck' },
  { value: 'carreta', label: 'Carreta' },
  { value: 'bitrem', label: 'Bitrem' },
  { value: 'rodotrem', label: 'Rodotrem' },
  { value: 'van', label: 'Van' },
  { value: 'toco', label: 'Toco' },
  { value: 'tres_quartos', label: '3/4' },
  { value: 'vuc', label: 'VUC' },
  { value: 'prancha', label: 'Prancha' },
  { value: 'furgão', label: 'Furgão' }
];

const tiposCarga = [
  { value: 'geral', label: 'Geral' },
  { value: 'granel_solido', label: 'Granel Sólido' },
  { value: 'granel_liquido', label: 'Granel Líquido' },
  { value: 'refrigerada', label: 'Refrigerada' },
  { value: 'perigosa', label: 'Perigosa' },
  { value: 'viva', label: 'Viva' },
  { value: 'indivisivel', label: 'Indivisível' },
  { value: 'container', label: 'Container' }
];

const tiposCarroceria = [
  { value: 'aberta', label: 'Aberta' },
  { value: 'fechada', label: 'Fechada' },
  { value: 'bau', label: 'Baú' },
  { value: 'sider', label: 'Sider' },
  { value: 'graneleira', label: 'Graneleira' },
  { value: 'tanque', label: 'Tanque' },
  { value: 'cegonha', label: 'Cegonha' },
  { value: 'prancha', label: 'Prancha' },
  { value: 'boiadeiro', label: 'Boiadeiro' },
  { value: 'cacamba', label: 'Caçamba' }
];

const tiposValor = [
  { value: 'fixo', label: 'Valor Fixo' },
  { value: 'a_combinar', label: 'A Combinar' },
  { value: 'tonelada', label: 'Por Tonelada' },
  { value: 'metro_cubico', label: 'Por m³' }
];

const unidadesPeso = [
  { value: 'kg', label: 'Quilogramas (kg)' },
  { value: 'ton', label: 'Toneladas (ton)' },
  { value: 'm3', label: 'Metros Cúbicos (m³)' }
];

const parseNumero = (valor) => {
  if (!valor || valor === '') return 0;
  const str = String(valor).trim().replace(/\./g, '').replace(',', '.');
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

export default function EditarCarga() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [carga, setCarga] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    titulo: '',
    tipo_carga: '',
    peso_valor: '',
    peso_unidade: 'ton',
    origem_cidade: '',
    origem_estado: '',
    destino_cidade: '',
    destino_estado: '',
    data_coleta: '',
    data_entrega: '',
    tipo_valor: 'fixo',
    valor_frete: '',
    tipo_veiculo: '',
    tipo_carroceria: '',
    descricao: ''
  });

  // Carregar carga do Supabase
  useEffect(() => {
    const carregarCarga = async () => {
      if (!id) {
        toast.error('ID da carga não informado');
        navigate('/minhas-cargas');
        return;
      }

      try {
        setIsLoading(true);
        
        // Tenta pegar do state primeiro (veio do MinhasCargas)
        const cargaDoState = location.state?.carga;
        
        if (cargaDoState) {
          console.log('✅ Carga do state:', cargaDoState.id);
          setCarga(cargaDoState);
          preencherForm(cargaDoState);
          setIsLoading(false);
          return;
        }

        // Busca do Supabase
        const { data, error } = await supabase
          .from('cargas')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;

        if (!data) {
          toast.error('Carga não encontrada');
          navigate('/minhas-cargas');
          return;
        }

        // Verifica se é da empresa logada
        const cargaEmpresaId = String(data.empresa_id || '').trim();
        const userId = String(user?.id || '').trim();
        const createdBy = String(data.created_by || '').trim();
        const userEmail = String(user?.email || '').trim();

        console.log('🔍 Comparação permissão:', { 
          cargaEmpresaId, 
          userId, 
          match: cargaEmpresaId === userId,
          createdBy, 
          userEmail,
          matchEmail: createdBy === userEmail 
        });

        if (cargaEmpresaId !== userId && createdBy !== userEmail) {
          toast.error('Você não tem permissão para editar esta carga');
          navigate('/minhas-cargas');
          return;
        }

        setCarga(data);
        preencherForm(data);
      } catch (err) {
        console.error('Erro ao carregar carga:', err);
        toast.error('Erro ao carregar carga');
        navigate('/minhas-cargas');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      carregarCarga();
    }
  }, [id, user, navigate, location.state]);

  const preencherForm = (data) => {
    setFormData({
      titulo: data.titulo || '',
      tipo_carga: data.tipo_carga || '',
      peso_valor: data.peso_valor || data.peso || '',
      peso_unidade: data.peso_unidade || 'ton',
      origem_cidade: data.origem_cidade || '',
      origem_estado: data.origem_estado || '',
      destino_cidade: data.destino_cidade || '',
      destino_estado: data.destino_estado || '',
      data_coleta: data.data_coleta || '',
      data_entrega: data.data_entrega || '',
      tipo_valor: data.tipo_valor || 'fixo',
      valor_frete: data.valor_frete || '',
      tipo_veiculo: data.tipo_veiculo || '',
      tipo_carroceria: data.tipo_carroceria || '',
      descricao: data.descricao || ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.titulo || !formData.origem_cidade || !formData.destino_cidade) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    setIsSaving(true);

    try {
      // Calcula valor total
      const valorFrete = parseNumero(formData.valor_frete);
      const peso = parseNumero(formData.peso_valor);
      let valorTotal = 0;

      if (formData.tipo_valor === 'tonelada' && peso > 0) {
        valorTotal = valorFrete * peso;
      } else if (formData.tipo_valor === 'metro_cubico') {
        valorTotal = valorFrete;
      } else if (formData.tipo_valor === 'fixo') {
        valorTotal = valorFrete;
      }

      console.log('🚀 Iniciando salvamento...');
      console.log('📤 ID carga:', id);
      console.log('📤 ID usuário:', user?.id);

      // ✅ MÉTODO 1: RPC atualizar_carga (bypass RLS, verificação manual)
      console.log('🔄 Tentando RPC atualizar_carga...');
      const { data: rpcResult, error: rpcError } = await supabase.rpc('atualizar_carga', {
        p_carga_id: id,
        p_titulo: formData.titulo,
        p_tipo_carga: formData.tipo_carga,
        p_peso_valor: parseNumero(formData.peso_valor),
        p_peso_unidade: formData.peso_unidade,
        p_origem_cidade: formData.origem_cidade,
        p_origem_estado: formData.origem_estado,
        p_destino_cidade: formData.destino_cidade,
        p_destino_estado: formData.destino_estado,
        p_data_coleta: formData.data_coleta || null,
        p_data_entrega: formData.data_entrega || null,
        p_tipo_valor: formData.tipo_valor,
        p_valor_frete: formData.tipo_valor === 'a_combinar' ? null : valorFrete,
        p_valor_total: valorTotal,
        p_tipo_veiculo: formData.tipo_veiculo,
        p_tipo_carroceria: formData.tipo_carroceria,
        p_descricao: formData.descricao
      });

      if (rpcError) {
        console.warn('⚠️ RPC erro:', rpcError);
        throw rpcError;
      }

      console.log('✅ RPC resultado:', rpcResult);

      if (rpcResult && rpcResult.success === true) {
        toast.success('✅ Carga atualizada com sucesso!');
        navigate('/minhas-cargas');
        return;
      }

      if (rpcResult && rpcResult.success === false) {
        console.error('❌ RPC retornou erro:', rpcResult.error);
        throw new Error(rpcResult.error || 'Erro ao atualizar carga');
      }

      // Se chegou aqui, RPC não retornou sucesso nem erro
      throw new Error('Resposta inesperada do servidor');

    } catch (err) {
      console.error('❌ Erro ao salvar:', err);
      console.error('❌ Detalhes completos:', {
        message: err.message,
        details: err.details,
        hint: err.hint,
        code: err.code
      });
      toast.error('Erro ao atualizar carga: ' + (err.message || err.details || 'Tente novamente'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate('/minhas-cargas')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Editar Carga</h1>
        <p className="text-slate-600 mb-8">#{id?.slice(-6)}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-blue-500" />
                Informações da Carga
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => handleChange('titulo', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Carga</Label>
                  <Select value={formData.tipo_carga} onValueChange={(v) => handleChange('tipo_carga', v)}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {tiposCarga.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Veículo</Label>
                  <Select value={formData.tipo_veiculo} onValueChange={(v) => handleChange('tipo_veiculo', v)}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {tiposVeiculo.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Peso/Quantidade</Label>
                  <Input
                    type="text"
                    value={formData.peso_valor}
                    onChange={(e) => handleChange('peso_valor', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidade</Label>
                  <Select value={formData.peso_unidade} onValueChange={(v) => handleChange('peso_unidade', v)}>
                    <SelectTrigger><SelectValue placeholder="Unidade" /></SelectTrigger>
                    <SelectContent>
                      {unidadesPeso.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5 text-blue-500" />
                Origem e Destino
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cidade Origem</Label>
                  <Input value={formData.origem_cidade} onChange={(e) => handleChange('origem_cidade', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>UF Origem</Label>
                  <Select value={formData.origem_estado} onValueChange={(v) => handleChange('origem_estado', v)}>
                    <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>
                      {estadosBrasil.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cidade Destino</Label>
                  <Input value={formData.destino_cidade} onChange={(e) => handleChange('destino_cidade', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>UF Destino</Label>
                  <Select value={formData.destino_estado} onValueChange={(v) => handleChange('destino_estado', v)}>
                    <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>
                      {estadosBrasil.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-blue-500" />
                Datas e Valor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Coleta</Label>
                  <Input type="date" value={formData.data_coleta} onChange={(e) => handleChange('data_coleta', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Data Entrega</Label>
                  <Input type="date" value={formData.data_entrega} onChange={(e) => handleChange('data_entrega', e.target.value)} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tipo de Valor</Label>
                <Select value={formData.tipo_valor} onValueChange={(v) => handleChange('tipo_valor', v)}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {tiposValor.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {formData.tipo_valor !== 'a_combinar' && (
                <div className="space-y-2">
                  <Label>Valor do Frete (R$)</Label>
                  <Input
                    type="text"
                    value={formData.valor_frete}
                    onChange={(e) => handleChange('valor_frete', e.target.value)}
                    placeholder="0,00"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/minhas-cargas')}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {isSaving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Salvar Alterações</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}